import { createTheme } from '@mui/material/styles';

const colors = {
  primary: '#2196F3',
  secondary: '#21CBF3',
  error: '#f44336',
  background: '#ffffff',
  text: {
    primary: '#333333',
    secondary: '#4a4a4a'
  }
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  }
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: '#4dabf5',
      dark: '#1769aa',
      contrastText: '#ffffff'
    },
    secondary: {
      main: colors.secondary,
      light: '#4dd4f5',
      dark: '#178da6',
      contrastText: '#ffffff'
    },
    error: {
      main: colors.error
    },
    background: {
      default: '#f8fafc',
      paper: colors.background
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary
    }
  },
  typography: {
    fontFamily: [
      'Pretendard',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    h6: {
      fontWeight: 600
    },
    subtitle1: {
      fontWeight: 500
    }
  },
  shape: {
    borderRadius: 12
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 6px 12px rgba(0, 0, 0, 0.10)',
    '0px 8px 16px rgba(0, 0, 0, 0.12)',
    ...Array(20).fill('none')
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          boxShadow: 'none',
          fontWeight: 600,
          padding: '8px 16px',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)'
          }
        },
        contained: {
          background: `linear-gradient(45deg, ${colors.primary} 30%, ${colors.secondary} 90%)`,
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.primary} 40%, ${colors.secondary} 80%)`
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
            },
            '&.Mui-focused': {
              transform: 'translateY(-1px)',
              boxShadow: '0 3px 8px rgba(0,0,0,0.15)'
            }
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.12)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&.MuiAppBar-root': {
            borderRadius: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#f8fafc'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
          overflow: 'hidden',
          marginTop: '8px'
        },
        list: {
          padding: '0'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          '&:last-child': {
            borderBottom: 'none'
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary}15`,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: `${colors.primary}25`
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12
        },
        icon: {
          transition: 'transform 0.3s ease',
          color: colors.text.primary
        },
        select: {
          paddingRight: '32px',
          '&.MuiSelect-select': {
            paddingRight: '32px'
          }
        }
      },
      defaultProps: {
        MenuProps: {
          PaperProps: {
            elevation: 3
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          transitionDuration: 200
        }
      }
    },
    MuiPopover: {
      defaultProps: {
        transitionDuration: 200
      }
    },
    MuiCssBaseline: {
      styleOverrides: `
        .MuiSelect-root.Mui-focused .MuiSelect-icon {
          transform: rotate(180deg);
        }
        
        .MuiMenu-paper {
          animation: dropdownAppear 0.2s ease-out forwards;
        }
        
        @keyframes dropdownAppear {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `
    }
  },
  breakpoints
});

const extendedTheme = {
  ...theme,
  spacing: (factor) => `${8 * factor}px`,
  customSpacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
    xlarge: '32px'
  },
  customBorderRadius: {
    small: '8px',
    medium: '12px',
    large: '30px'
  },
  customColors: {
    ...colors,
    background: {
      gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }
  }
};

export const getGradientText = () => ({
  background: `linear-gradient(45deg, ${colors.primary} 30%, ${colors.secondary} 90%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700
});

export { colors, breakpoints };
export default extendedTheme;